/**
 * Phase 45 Plan 45-08 (D-14) — TaskDetailSlideOver time entries section.
 *
 * Read-only table of Entry rows scoped to a single task. Logging continues
 * via the timesheet flow (LogTimePopover from MyTasks); the slide-over does
 * not allow inline edits here.
 */
import { useTaskTimeEntries } from '../../hooks/useTaskTimeEntries';
import { formatDisplayDate } from '../../utils/format';

interface TaskTimeEntriesProps {
  taskId: number;
}

function formatHours(value: number | null): string {
  if (value == null) return '0h';
  // Hours are stored as floats; clamp to one decimal for display.
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}h` : `${rounded.toFixed(1)}h`;
}

function userLabel(user: {
  firstName: string | null;
  lastName: string | null;
} | null): string {
  if (!user) return '—';
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
}

export function TaskTimeEntries({ taskId }: TaskTimeEntriesProps) {
  const { data = [], isLoading, isError } = useTaskTimeEntries(taskId);

  const total = data.reduce((sum, e) => sum + (e.hours ?? 0), 0);

  return (
    <section
      aria-label="Time entries"
      className="pt-3 border-t border-ms-border-subtle"
      data-testid="time-entries-section"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
          Time entries
        </h3>
        {data.length > 0 && (
          <span className="text-xs text-ms-text-secondary tabular-nums">
            Total {formatHours(total)}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-1" aria-label="Loading time entries">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-6 bg-ms-canvas rounded animate-pulse"
              data-testid="time-entries-skeleton"
            />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-ms-text-muted italic">
          Couldn&apos;t load time entries.
        </p>
      ) : data.length === 0 ? (
        <p className="text-sm text-ms-text-muted italic">No time logged yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-ms-text-secondary">
                <th className="text-left font-medium py-1 pr-3">Date</th>
                <th className="text-left font-medium py-1 pr-3">User</th>
                <th className="text-right font-medium py-1 pr-3">Hours</th>
                <th className="text-left font-medium py-1">Note</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-t border-ms-border-subtle"
                  data-testid={`time-entry-row-${entry.id}`}
                >
                  <td className="py-1 pr-3 text-ms-text tabular-nums">
                    {formatDisplayDate(entry.date)}
                  </td>
                  <td className="py-1 pr-3 text-ms-text">{userLabel(entry.user)}</td>
                  <td className="py-1 pr-3 text-ms-text text-right tabular-nums">
                    {formatHours(entry.hours)}
                  </td>
                  <td className="py-1 text-ms-text-secondary truncate max-w-[16rem]">
                    {entry.description ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
